import type { ReactNode } from 'react';
import type { BladeDescriptor, PresentationMode } from './types';

// The mount seam (HUB-275): a blade renders whatever ViewPrimitive its `key`
// resolves to. The shell only positions blades + mounts a descriptor's view, so
// the view-primitive catalog (HUB-276) and binding renderer (HUB-277) stay
// independent of the shell.

/** Context handed to a view-primitive when it renders inside a blade. */
export interface BladeContext {
  descriptor: BladeDescriptor;
  index: number;
  /** open a child blade from this one (truncates the path to the right). */
  openChild: (blade: BladeDescriptor) => void;
  /** replace THIS blade in place (keeps the path to its left, drops its
   *  descendants, pushes `blade`). Used for sibling navigation — e.g. the
   *  audit compare screen's prev/next stepping through a batch's cases. */
  replaceSelf: (blade: BladeDescriptor) => void;
  /** switch the whole canvas between explorer (columns) and focus (full-bleed).
   *  A drill-into-review action pairs openChild with setMode('focus'). */
  setMode: (mode: PresentationMode) => void;
  /** close this blade (and its descendants). */
  close: () => void;
}

/** A mountable view, registered by key. */
export interface ViewPrimitive {
  key: string;
  render: (ctx: BladeContext) => ReactNode;
  /** Chrome to fall back on when the descriptor carries none.
   *
   *  The URL codec round-trips only navigable identity (key + entityId +
   *  params) — display strings and width are not in the path. So a blade
   *  decoded from a deep link or a refresh arrives titled with its own key and
   *  at the default width, which for the audit views means an "audit-batches"
   *  header and a layout narrower than the view was built for. Resolving these
   *  fresh by key at render time (as `chromeless` already does) restores them
   *  without widening the URL contract. */
  defaultTitle?: string;
  defaultEyebrow?: string;
  defaultWidth?: BladeDescriptor['width'];
  /** Suppress the shell blade-head (eyebrow + title) so the view renders its own
   *  consolidated header. Resolved fresh by key at render time, so it holds even
   *  when a blade descriptor was persisted before this view opted in. */
  chromeless?: boolean;
}

const registry = new Map<string, ViewPrimitive>();

export function registerViewPrimitive(p: ViewPrimitive): void {
  registry.set(p.key, p);
}

export function getViewPrimitive(key: string): ViewPrimitive | undefined {
  return registry.get(key);
}

/** Test helper: reset the registry. */
export function clearViewPrimitives(): void {
  registry.clear();
}
